import React, { RefObject, useMemo } from 'react'
import { Form, Select as SelectAntd, Spin } from 'antd'
import {
  OptionProps as OptionPropsAntd,
  SelectProps as SelectPropsAntd
} from 'antd/lib/select'
import { FormItemProps as FormItemPropsAnd } from 'antd/lib/form'
import dp from 'dot-prop-immutable'

import { useT } from 'src/core/hooks/use-translation'

export type Option = OptionPropsAntd & {
  label: string
}

export type SelectProps = Omit<SelectPropsAntd, 'defaultValue'> &
  Pick<FormItemPropsAnd, 'label' | 'required'> & {
    formikProps: any
    innerRef?: RefObject<SelectAntd<string>>
    name: string
    style?: React.CSSProperties
    options: Option[]
  }

type Props = SelectProps & {
  dataTestId: string
}

function Select({
  formikProps,
  innerRef,
  label,
  loading,
  name,
  onChange,
  options,
  placeholder,
  required = true,
  style = { width: '100%' },
  size = 'large',
  dataTestId,
  ...props
}: Props) {
  const { t } = useT()
  const error = useMemo(() => {
    return dp.get(formikProps.errors, name)
  }, [formikProps.errors, name])

  const touched = useMemo(() => {
    return dp.get(formikProps.touched, name)
  }, [formikProps.touched, name])

  const value = useMemo(() => {
    return dp.get(formikProps.values, name)
  }, [formikProps.values, name])

  function handleChange(
    value: string,
    option: React.ReactElement<any> | React.ReactElement<any>[]
  ) {
    formikProps.setFieldValue(name, value)
    if (onChange) {
      onChange(value, option)
    }
  }

  return (
    <Form.Item
      colon={false}
      help={touched && typeof error === 'string' ? t(error) : ''}
      label={t(label)}
      required={required}
      validateStatus={
        error && touched ? 'error' : !error && touched ? 'success' : ''
      }
      hasFeedback
    >
      <SelectAntd
        {...props}
        allowClear={!required}
        data-test-id={`${dataTestId}-list`}
        loading={loading}
        notFoundContent={loading ? <Spin size="small" /> : null}
        onBlur={() =>
          formikProps.setTouched(dp.set({ ...formikProps.touched }, name, true))
        }
        onChange={handleChange}
        placeholder={t(placeholder)}
        ref={innerRef}
        size={size}
        style={style}
        value={
          Array.isArray(value) ? value : value ? `${value}` : (undefined as any)
        }
      >
        {options.map(({ title, label, ...optProps }, index) => (
          <SelectAntd.Option
            {...optProps}
            data-test-id={`${dataTestId}-item-${label}`}
            key={index}
            title={title || label}
          >
            {label}
          </SelectAntd.Option>
        ))}
      </SelectAntd>
    </Form.Item>
  )
}

export default Select
